import React, { useCallback, useEffect, useState } from 'react';
import apiService from '../services/ApiService';
import desktopAgentLifecycleService, { DesktopAgentLifecycle } from '../services/DesktopAgentLifecycleService';

interface DesktopAgentDevice {
    id: string;
    userId: number;
    userEmail: string;
    deviceIdentifier: string;
    deviceName: string;
    agentVersion: string;
    platform: string;
    runtimeMode: string;
    capabilities: string[];
    pairedAtUtc: string;
    lastSeenAtUtc?: string | null;
    revokedAtUtc?: string | null;
    isOnline: boolean;
}

interface PairingCode {
    code: string;
    expiresAtUtc: string;
}

const errorMessage = (error: unknown): string =>
    error instanceof Error ? error.message : 'Operazione non riuscita.';

const formatDate = (value?: string | null): string =>
    value ? new Date(value).toLocaleString('it-IT') : 'Mai';

const DesktopAgentPairingPanel: React.FC<{
    healthPort: number;
    onAgentChanged: () => Promise<void>;
}> = ({ healthPort, onAgentChanged }) => {
    const [lifecycle, setLifecycle] = useState<DesktopAgentLifecycle | null>(null);
    const [devices, setDevices] = useState<DesktopAgentDevice[]>([]);
    const [pairingCode, setPairingCode] = useState<PairingCode | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const refresh = useCallback(async () => {
        setError('');
        const [localResult, devicesResult] = await Promise.allSettled([
            desktopAgentLifecycleService.lifecycle(healthPort),
            apiService.adminGetDesktopAgentDevices<DesktopAgentDevice>(),
        ]);

        if (localResult.status === 'fulfilled') setLifecycle(localResult.value);
        else setLifecycle(null);
        if (devicesResult.status === 'fulfilled') setDevices(devicesResult.value);
        else setError(errorMessage(devicesResult.reason));
    }, [healthPort]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const pair = async () => {
        setBusy(true);
        setError('');
        setMessage('');
        try {
            const pairing = await apiService.adminStartDesktopAgentPairing<PairingCode>();
            setPairingCode(pairing);
            await desktopAgentLifecycleService.pair(
                healthPort,
                apiService.getApiBaseUrl(),
                pairing.code,
            );
            setMessage('Dispositivo associato. Il primo heartbeat è stato registrato.');
            setPairingCode(null);
            await refresh();
            await onAgentChanged();
        } catch (pairingError) {
            setError(errorMessage(pairingError));
        } finally {
            setBusy(false);
        }
    };

    const toggleAutoStart = async () => {
        if (!lifecycle) return;
        setBusy(true);
        setError('');
        try {
            await desktopAgentLifecycleService.setAutoStart(healthPort, !lifecycle.autoStartEnabled);
            await refresh();
        } catch (toggleError) {
            setError(errorMessage(toggleError));
        } finally {
            setBusy(false);
        }
    };

    const unpairLocal = async () => {
        setBusy(true);
        setError('');
        try {
            await desktopAgentLifecycleService.unpair(healthPort);
            setMessage('IA Desktop Agent disassociato da questo PC.');
            await refresh();
        } catch (unpairError) {
            setError(errorMessage(unpairError));
        } finally {
            setBusy(false);
        }
    };

    const revoke = async (id: string) => {
        setBusy(true);
        setError('');
        try {
            await apiService.adminRevokeDesktopAgent(id);
            await refresh();
        } catch (revokeError) {
            setError(errorMessage(revokeError));
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="card shadow-sm mb-4 desktop-agent-pairing">
            <div className="card-header bg-body d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div>
                    <h5 className="mb-1">Avvio e associazione dispositivo</h5>
                    <div className="small text-body-secondary">Configurazione per-user del PC corrente e dispositivi registrati.</div>
                </div>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => void refresh()} disabled={busy}>
                    <i className="bi bi-arrow-clockwise me-2" />Aggiorna
                </button>
            </div>
            <div className="card-body">
                {error && <div className="alert alert-danger py-2"><i className="bi bi-exclamation-octagon me-2" />{error}</div>}
                {message && <div className="alert alert-success py-2"><i className="bi bi-check-circle me-2" />{message}</div>}

                <div className="row g-3 mb-4">
                    <div className="col-md-4">
                        <div className="desktop-agent-setting">
                            <i className="bi bi-windows" />
                            <div className="flex-grow-1">
                                <strong>System tray</strong>
                                <small>{lifecycle?.trayAvailable ? 'Disponibile e attiva' : 'Agent locale non rilevato'}</small>
                            </div>
                            <span className={`badge ${lifecycle?.trayAvailable ? 'text-bg-success' : 'text-bg-secondary'}`}>
                                {lifecycle?.trayAvailable ? 'Attiva' : '—'}
                            </span>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="desktop-agent-setting">
                            <i className="bi bi-power" />
                            <div className="flex-grow-1">
                                <strong>Avvio al login</strong>
                                <small>Registro Windows dell&apos;utente corrente</small>
                            </div>
                            <button
                                className={`btn btn-sm ${lifecycle?.autoStartEnabled ? 'btn-success' : 'btn-outline-secondary'}`}
                                onClick={() => void toggleAutoStart()}
                                disabled={busy || !lifecycle?.autoStartSupported}>
                                {lifecycle?.autoStartEnabled ? 'Abilitato' : 'Abilita'}
                            </button>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="desktop-agent-setting">
                            <i className="bi bi-link-45deg" />
                            <div className="flex-grow-1">
                                <strong>Pairing</strong>
                                <small>{lifecycle?.pairing.isPaired ? lifecycle.pairing.deviceName : 'Nessun dispositivo associato'}</small>
                            </div>
                            {lifecycle?.pairing.isPaired ? (
                                <button className="btn btn-sm btn-outline-danger" onClick={() => void unpairLocal()} disabled={busy}>Disassocia</button>
                            ) : (
                                <button className="btn btn-sm btn-primary" onClick={() => void pair()} disabled={busy || !lifecycle}>
                                    {busy ? 'Associazione…' : 'Associa questo PC'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {pairingCode && (
                    <div className="alert alert-warning d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <span>Codice one-time valido fino alle {formatDate(pairingCode.expiresAtUtc)}</span>
                        <code className="desktop-agent-pairing__code">{pairingCode.code}</code>
                    </div>
                )}

                <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="mb-0">Dispositivi registrati</h6>
                    <span className="small text-body-secondary">Online se heartbeat negli ultimi 2 minuti</span>
                </div>
                {devices.length === 0 ? (
                    <div className="border rounded p-3 text-body-secondary small">Nessun IA Desktop Agent registrato.</div>
                ) : (
                    <div className="table-responsive">
                        <table className="table align-middle mb-0">
                            <thead><tr><th>Dispositivo</th><th>Utente</th><th>Versione</th><th>Ultimo heartbeat</th><th>Stato</th><th /></tr></thead>
                            <tbody>
                                {devices.map(device => (
                                    <tr key={device.id}>
                                        <td><strong>{device.deviceName}</strong><div className="small text-body-secondary">{device.platform}</div></td>
                                        <td className="small">{device.userEmail}</td>
                                        <td><code>{device.agentVersion}</code></td>
                                        <td className="small">{formatDate(device.lastSeenAtUtc)}</td>
                                        <td>
                                            <span className={`badge ${device.revokedAtUtc ? 'text-bg-danger' : device.isOnline ? 'text-bg-success' : 'text-bg-secondary'}`}>
                                                {device.revokedAtUtc ? 'Revocato' : device.isOnline ? 'Online' : 'Offline'}
                                            </span>
                                        </td>
                                        <td className="text-end">
                                            {!device.revokedAtUtc && (
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => void revoke(device.id)} disabled={busy}>
                                                    Revoca
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </section>
    );
};

export default DesktopAgentPairingPanel;
